import React, { useState, useEffect, useRef, useMemo } from "react";
import DateRangePicker from "../utils/GenericDateRangeFilter/GenericDateRangeFilter";
import { useGetOrgInsights, usePlant, useUserDetail, useRegionalOffices, useStateOffices } from "../../services/hooks";
import { Button, Dropdown,} from "../utils/index";
import { useGetVehicleMovementStatsMutation } from "../../api/vmsSlice";
import BarChart from "./BarChart";
import theme, { buttonClasses } from "../utils/theme";
import "./VehicleMovement.css";
import PieChart from "./PieChart";
import { capitalizeCamelCase } from "../../services/functions";
import GenericDownloadTable from "../utils/GenericDownloadTable";
import WarehouseTripInsightsTable from "./PlantSummary";
const VehicleMovementStats = () => {
  // ---- helpers function to normalize day bounds ----
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  const today = new Date()
  // Default last 7 days: start=00:00 six days ago, end=23:59:59.999 today
  const now = new Date();
  const todayStart = startOfDay(now);
  const todayEnd = endOfDay(now);
  const lastWeekStart = new Date(todayStart);
  lastWeekStart.setDate(todayStart.getDate() - 6);

  const [dateRange, setDateRange] = useState({
    start: lastWeekStart,
    end: todayEnd,
  });
  const [filters, setFilters] = useState({ plantIds: [] });
  const [barChartData, setBarChartData] = useState([]);
  const [pieChartData, setPieChartData] = useState([]);
  const { userDetail } = useUserDetail();
  const [orgStats, setOrgStats]= useState([])
  const { plantOptions, plants } = usePlant({});
  const { states, statesOptions } = useStateOffices({});
  const updateDataRef = useRef(null);
  const [isModalForComment, setIsModalForComment] = useState(false);
  const onModalForCommentOpen = () => setIsModalForComment(true);
  const onModalForCommentClose = () => {
    setIsModalForComment(false);
    if (updateDataRef.current) {
      updateDataRef.current = null;
    }
  };
     
  const [isModalForView, setIsModalForView] = useState(false);
  const onModalForViewOpen = () => setIsModalForView(true);
  const onModalForViewClose = () => {
    setIsModalForView(false);
    if (updateDataRef.current) {
      updateDataRef.current = null;
    }
  };

  const [filteredROptions, setFilteredROptions] = useState([]);
  const [filteredStatesOptions, setFilteredStatesOptions] = useState([]);
  const [filteredPlantOptions, setFilteredPlantOptions] = useState(plantOptions);
  const { regionalOfficesOptions, regionalOffices } = useRegionalOffices({});
  const [getVehicleMovementStats, { isLoading: statsLoading }] = useGetVehicleMovementStatsMutation();
  const {orgInsightsData, count, message, isLoading, getOrgInsights}= useGetOrgInsights()
  const selectedPlants = plantOptions.filter((opt) => filters.plantIds.includes(opt.value));


  // API CALLING
  const fetchData = async (plantIds, startDate, endDate) => {
    try {
      const result = await getVehicleMovementStats({plantIds, start: startDate.toISOString(), end: endDate.toISOString(),}).unwrap();      
      const orgInsightResults= await getOrgInsights({ startTime: startOfDay(today).toISOString(), version:'v2',
      endTime: endOfDay(today).toISOString()}).unwrap()
      const orgData = orgInsightResults?.data;
      // Filter org insights to only show plants the user has permission for
      const allowedPlantIds = userDetail?.plantIds;
      if (allowedPlantIds?.length) {
        const filterByPlant = (arr) => arr?.filter((d) => allowedPlantIds.includes(d.plantId)) || [];
        setOrgStats({
          today: filterByPlant(orgData?.today),
          yesterday: filterByPlant(orgData?.yesterday),
        });
      } else {
        setOrgStats(orgData);
      }      
      const validateBarData = validateHasAllDay(result.barChartData, startDate, endDate, ["totalGateIn", "totalWeighbridgeIn", "totalWeighbridgeOut", "totalGateOut"]);  

      setBarChartData(validateBarData);
      setPieChartData(result.pieChartData || []);
      setDateRange({ start: startDate, end: endDate });
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  };

  // Build initial dropdown data... these dropdown data will be filled up by the data coming up from the plants 
  useEffect(() => {
    if (plants.length > 0) {
      // Get unique Regional Offices from plants
      const roOptions = Array.from(new Set(plants.map((p) => p.regionalOffice).filter(Boolean))).map((ro) => ({ label: ro, value: ro }));

      // Get unique States from plants
      // .filter(Boolean) used here for removing the all "", null , undefined value 
      const stateOptions = Array.from(new Set(plants.map((p) => p.state).filter(Boolean))).map((st) => ({ label: st, value: st }));

      // Set dropdowns initially
      setFilteredROptions(roOptions);
      setFilteredStatesOptions(stateOptions);

      // Plants dropdown initially - all plants
      setFilteredPlantOptions(plants.map((p) => ({ label: p.plantName, value: p._id })));
      setFilters((prev) => {
        let updated = { ...prev };

        if (roOptions.length === 1) {
          updated.regionalOffices = [roOptions[0].value];
        }
        if (stateOptions.length === 1) {
          updated.states = [stateOptions[0].value];
        }
        if (plantOptions.length === 1) {
          updated.plantIds = [plantOptions[0].value];
        }

        return updated;
    });
    }
  }, [plants]);
  
  useEffect(() => {
    if (plantOptions.length > 0 && filters.plantIds.length === 0) {
      const allPlantIds = plantOptions.map((opt) => opt.value);
      if (!dateRange.start || !dateRange.end) {
        // ensure normalized defaults
        const _todayEnd = endOfDay(new Date());
        const _lastWeekStart = startOfDay(new Date());
        _lastWeekStart.setDate(_lastWeekStart.getDate() - 6);

        setDateRange({ start: _lastWeekStart, end: _todayEnd });
        fetchData(allPlantIds, _lastWeekStart, _todayEnd);
      } else {
        // use whatever current dateRange is
        fetchData(allPlantIds, dateRange.start, dateRange.end);
      }
    }
  }, [plantOptions]); // keep deps minimal

  const handleDateRangeSelect = (range) => {
  if (!range) {
    // when clear icon clicked
    setDateRange({ start: null, end: null });
    return;
  }

  // Range may contain Date objects or serializable strings
  const startDate = range.startDate instanceof Date ? range.startDate : new Date(range.startDate);
  const endDate = range.endDate instanceof Date ? range.endDate : new Date(range.endDate);
    setDateRange({
      start: startDate,
      end: endDate,
    });
  };

   const onDropdownChange = (selectedOptions, name) => {
    if (name === "regionalOffice") {
      const selectedROs = selectedOptions?.map((opt) => opt.value) || [];

      // Filter states based on selected ROs
      const filteredStates = Array.from(
        new Set(
          plants
            .filter((p) => !selectedROs.length || selectedROs.includes(p.regionalOffice))
            .map((p) => p.state)
            .filter(Boolean)   // remove empty/null/undefined
        )
      ).map((st) => ({ label: st, value: st }));
      setFilteredStatesOptions(filteredStates);

      // Filter plants based on selected ROs + existing state filter
      const filteredPlants = plants
        .filter(
          (p) =>
            (!filters.states?.length || filters.states.includes(p.state)) &&
            (!selectedROs.length || selectedROs.includes(p.regionalOffice))
        )
        .map((p) => ({ label: p.plantName, value: p._id }));
      setFilteredPlantOptions(filteredPlants);

      setFilters((prev) => ({
        ...prev,
        regionalOffices: selectedROs,
        states:
          prev.states?.filter((st) => filteredStates.some((fs) => fs.value === st)) || [],
        // reset plantIds if RO is cleared
        plantIds: selectedROs.length
          ? prev.plantIds.filter((pid) =>
              filteredPlants.some((fp) => fp.value === pid)
            )
          : [],
      }));
    } else if (name === "state") {
      const selectedStates = selectedOptions?.map((opt) => opt.value) || [];

      // Filter plants based on selected States + existing RO filter
      const filteredPlants = plants
        .filter(
          (p) =>
            (!selectedStates.length || selectedStates.includes(p.state)) &&
            (!filters.regionalOffices?.length ||
              filters.regionalOffices.includes(p.regionalOffice))
        )
        .map((p) => ({ label: p.plantName, value: p._id }));
      setFilteredPlantOptions(filteredPlants);

      setFilters((prev) => ({
        ...prev,
        states: selectedStates,
        // reset plantIds if State is cleared
        plantIds: selectedStates.length
          ? prev.plantIds.filter((pid) =>
              filteredPlants.some((fp) => fp.value === pid)
            )
          : [],
      }));
    } else if (name === "plantId") {
      const selectedPlantIds = selectedOptions?.map((opt) => opt.value) || [];
      setFilters((prev) => ({
        ...prev,
        plantIds: selectedPlantIds,
      }));
    }
  };

  const handleResetFilters = () => {
    // default last 7 days data when reset button is clicked
    const _todayEnd = endOfDay(new Date());
    const _lastWeekStart = startOfDay(new Date());
    _lastWeekStart.setDate(_lastWeekStart.getDate() - 6);
    const allPlantIds = plantOptions.map((opt) => opt.value);
    setFilters({ plantIds: [] }); // clears all filters
    setFilteredROptions(
      Array.from(new Set(plants.map((p) => p.regionalOffice).filter(Boolean))).map((ro) => ({
        label: ro,
        value: ro,
      }))
    );
    setFilteredStatesOptions(
      Array.from(new Set(plants.map((p) => p.state).filter(Boolean))).map((st) => ({
        label: st,
        value: st,
      }))
    );
    setFilteredPlantOptions(plants.map((p) => ({ label: p.plantName, value: p._id })));
    setDateRange({ start: _lastWeekStart, end: _todayEnd });
    fetchData(allPlantIds, _lastWeekStart, _todayEnd);
  };

  const handleFilterChange = async () => {
    if (!dateRange.start || !dateRange.end) {
      return;
    }
    const hasLocationFilters = filters.regionalOffices?.length || filters.states?.length;
    const plantIdsToSubmit =
      filters.plantIds.length > 0
        ? filters.plantIds
        : hasLocationFilters
          ? filteredPlantOptions.map((opt) => opt.value)
          : filters.plantIds;

    fetchData(plantIdsToSubmit, dateRange?.start, dateRange?.end)
  };

const validateHasAllDay = (data, start, end, keyArr) => {  
  const generateDateRange = (startDate, endDate) => {
    // Convert to UTC dates to avoid timezone issues
    const startUTC = Date.UTC(
      startDate.getFullYear(), 
      startDate.getMonth(), 
      startDate.getDate()
    );
    
    const endUTC = Date.UTC(
      endDate.getFullYear(), 
      endDate.getMonth(), 
      endDate.getDate()
    );
    
    const dates = [];
    const oneDay = 24 * 60 * 60 * 1000; // milliseconds in one day
    
    for (let dateUTC = startUTC; dateUTC <= endUTC; dateUTC += oneDay) {
      const date = new Date(dateUTC);
      dates.push(date.toISOString().split("T")[0]);
    }
    
    return dates;
  };
  
  const allDates = generateDateRange(start, end);
  const dataMap = data?.reduce((map, item) => {
    map[item._id] = { ...item };
    return map;
  }, {});
  
  return allDates.map((date) => {
    let res = { _id: date };
    for (let key of keyArr) {
      res[key] = dataMap[date]?.[key] || 0;
    }
    return res;
  });
};
  const getPieChartDataForCategory = (category) => {
    const categoryData = pieChartData.find((item) => item._id === category);
    if (!categoryData) return [];
    return [
      { name: "Camera", value: categoryData.totalCamera || 0 },
      { name: "RFID", value: categoryData.totalRfid || 0 },
      { name: "Manual", value: categoryData.totalManual || 0 },
    ];
  };

  const excelHeaders = useMemo(() => [
      "Warehouse Name",
      "Total",
      "Total Ongoing",
      "Ongoing % Complete",
      "Ongoing Trips vs Yesterday",
      "Total Completed",
      "Completed % Complete",
      "Completed Trips vs Yesterday",
    ], []);
    const excelRows = useMemo(() => {
      const today = orgStats?.today || [];
      const yesterday = orgStats?.yesterday || [];

      const yesterdayMap = {};
      yesterday.forEach(d => {
        if (d?.plantId) yesterdayMap[d.plantId] = d;
      });

      return today.map(d => {
        const y = yesterdayMap[d.plantId] || {};
        return [
          d.plantName,
          d.total,
          d.ongoing,
          d.total ? ((d.ongoing / d.total) * 100).toFixed(1) : 0,
          d.ongoing - (y.ongoing ?? 0),
          d.completed,
          d.total ? ((d.completed / d.total) * 100).toFixed(1) : 0,
          d.completed - (y.completed ?? 0),
        ];
      });
    }, [orgStats]);

  return (
    <div className="new-dashboard-container">
      <div className="contentpanel">
        <div className="dashboard-row cust-row flex-algn-cent">
          <div className="cust-col-5" id="dash">
            <h1 className="title">Dashboard</h1>
          </div>
          <div className="cust-col-5 flex-jc-end"></div>
        </div>
        <hr className="bgg" />
      </div>

      <div className="filter-section" style={{ display: "flex", gap: "0.2rem", marginTop:"1.5rem", flexWrap:"wrap" }}>
        <div style={{maxWidth:"242px", minWidth:"242px"}}>
          <Dropdown
            id="regionalOffice"
            name="regionalOffice"
            label="Regional Office"
            onChange={onDropdownChange}
            options={filteredROptions}
            value={regionalOfficesOptions.filter((opt) =>filters.regionalOffices?.includes(opt.value))}
            multiple
          />
        </div>
            
        <div style={{maxWidth:"222px", minWidth:"222px"}}>
          <Dropdown
            id="state"
            name="state"
            label="State"
            onChange={onDropdownChange}
            options={[...filteredStatesOptions].sort((a, b) => a.label.localeCompare(b.label))}
            value={filteredStatesOptions.filter((opt) => filters.states?.includes(opt.value))}
            multiple
          />
        </div>
            
        <div style={{maxWidth:"242px", minWidth:"242px"}}>
          <Dropdown
            id="plantFilter"
            name="plantId"
            label="Warehouses"
            onChange={onDropdownChange}
            options={filteredPlantOptions}
            value={selectedPlants}
            multiple
            disableCloseOnSelect={true}
          />
        </div>
            
        <div style={{maxWidth:"270px", minWidth:"244px"}}>
          <DateRangePicker
            onDateRangeSelect={handleDateRangeSelect}
            initialStartDate={dateRange.start}
            initialEndDate={dateRange.end}
          />
        </div>   
        <div style={{ minWidth: "175px", display:"flex", gap: "0.2rem", maxHeight:"41px"}}> 
          <Button
            text="Reset"
            onClick={handleResetFilters}
            style={{maxWidth: "85px", ...buttonClasses.lynkitOrangeEmpty}}
           
          />
          <Button
            text={statsLoading ? "Loading..." : "Submit"}
            onClick={handleFilterChange}
            disabled={!dateRange.start ||!dateRange.end ||statsLoading}
            style={{maxWidth: "100px", ...buttonClasses.lynkitOrangeFill}}
          />  
        </div>
      </div>
      <div>
            <div
              className="vehicleMovementContainer"
              style={{
                borderRadius: "8px",
                padding: "15px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              <div className="vehicleMovementHeading">
                Vehicle Movement
              </div>
              {statsLoading &&  (
                <div className="chart-skeleton" ></div>
              )}
              {!statsLoading && barChartData.length > 0 && (
                <BarChart dataSet={barChartData} chartWidth="100%" />
              )}
            </div>

            {!statsLoading && pieChartData.length > 0 && (
              <div className="eventHeading"
                style={{
                  borderRadius: "8px",
                  padding: "15px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  marginTop: "1.5rem",
                }}
              >
                Events
                <div
                  className="pie-charts-container"
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "20px",
                    marginTop: "20px",
                  }}
                >
                  {pieChartData.map((categoryData) => (categoryData?._id == "godown" ? null :                    
                    <div key={categoryData._id} className="pie-chart-card"
                      style={{
                        flex: "1",
                        minWidth: "274px",
                        maxWidth: "400px",
                        backgroundColor: "#fff",
                        borderRadius: "8px",
                        padding: "15px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      }}
                    >
                      <div className="eventSubHeading">{(capitalizeCamelCase(categoryData._id))}</div>
                      <PieChart dataSet={getPieChartDataForCategory(categoryData._id)} chartWidth="100%"/>
                    </div>
                  ))}
                </div>
              </div>
            )}

        {userDetail?.userRoleHierarchy == 0 && !isLoading && orgStats && (
          <div style={{borderRadius: "8px", padding: "15px 9px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)", marginTop:"1.5rem"}}>
          <div className="dashboard-row cust-row flex-algn-cent">
            <div className="cust-col-5" id="dash" style={{width:"100%", display:"flex", justifyContent:"space-between"}}>
              <h1 className="title">Warehouse Trips Summary</h1>
                <div style={{minWidth:"120px"}}>
                  <GenericDownloadTable
                    headers={excelHeaders}
                    rows={excelRows}
                    fileName="Warehouse_Trip_Insights"
                  />
                </div>
            </div> </div>
            <WarehouseTripInsightsTable data={orgStats}/>
          </div>
        )}    
      </div>
    </div>
  );
};

export default VehicleMovementStats;
